import Dexie, { type EntityTable } from 'dexie';
import type { Brand } from '@/entities/brand';
import type { Job } from '@/entities/job';
import type { Prediction } from '@/entities/prediction';
import type { ResolvedPrompt, Template } from '@/entities/template';

export interface ProductImageRow {
  id: string;
  brandId: string;
  filename: string;
  blob: Blob;
  uploadedReplicateUrl?: string;
  createdAt: number;
}

export interface GeneratedImageRow {
  id: string;
  jobId: string;
  predictionId: string;
  templateId: number;
  templateName: string;
  blob: Blob;
  contentType: string;
  createdAt: number;
}

export interface ResolvedPromptRow extends ResolvedPrompt {
  id: string;
  brandId: string;
  jobId?: string;
  createdAt: number;
}

export interface UserTemplateRow extends Template {
  source: 'built-in' | 'user';
  createdAt: number;
}

export class BrandGeneratorDB extends Dexie {
  brands!: EntityTable<Brand, 'id'>;
  jobs!: EntityTable<Job, 'id'>;
  predictions!: EntityTable<Prediction, 'id'>;
  productImages!: EntityTable<ProductImageRow, 'id'>;
  generatedImages!: EntityTable<GeneratedImageRow, 'id'>;
  resolvedPrompts!: EntityTable<ResolvedPromptRow, 'id'>;
  userTemplates!: EntityTable<UserTemplateRow, 'id'>;

  constructor() {
    super('brand-generator');

    this.version(1).stores({
      brands: 'id, name, createdAt',
      jobs: 'id, brandId, status, createdAt',
      predictions: 'id, status, startedAt',
      productImages: 'id, brandId, createdAt',
      generatedImages: 'id, jobId, predictionId, templateId, createdAt',
      resolvedPrompts: 'id, brandId, jobId, createdAt',
      userTemplates: 'id, source, createdAt',
    });

    // v2: redesigned jobs table — DAG with parentJobId/dependsOn, generic
    // kind/status, per-kind input/output. The v1 jobs table is incompatible
    // with the new shape; clear it on upgrade. Other tables are unchanged.
    this.version(2)
      .stores({
        brands: 'id, name, createdAt',
        jobs: 'id, parentJobId, status, brandId, kind, createdAt',
        predictions: 'id, status, startedAt',
        productImages: 'id, brandId, createdAt',
        generatedImages: 'id, jobId, predictionId, templateId, createdAt',
        resolvedPrompts: 'id, brandId, jobId, createdAt',
        userTemplates: 'id, source, createdAt',
      })
      .upgrade(async (tx) => {
        await tx.table('jobs').clear();
      });

    // v3: BrandDNA gained productType. Backfill existing brands to 'physical'
    // (the prior implicit assumption); users can flip it to 'app' / 'service'
    // in the DNA editor.
    this.version(3)
      .stores({
        brands: 'id, name, createdAt',
        jobs: 'id, parentJobId, status, brandId, kind, createdAt',
        predictions: 'id, status, startedAt',
        productImages: 'id, brandId, createdAt',
        generatedImages: 'id, jobId, predictionId, templateId, createdAt',
        resolvedPrompts: 'id, brandId, jobId, createdAt',
        userTemplates: 'id, source, createdAt',
      })
      .upgrade(async (tx) => {
        await tx.table('brands').toCollection().modify((b: { dna?: { productType?: string } }) => {
          if (b.dna && !b.dna.productType) {
            b.dna.productType = 'physical';
          }
        });
      });

    // v4: BrandDNA was trimmed — drop fields no template referenced and that
    // promptModifier already absorbs. Existing brands keep all other fields.
    this.version(4)
      .stores({
        brands: 'id, name, createdAt',
        jobs: 'id, parentJobId, status, brandId, kind, createdAt',
        predictions: 'id, status, startedAt',
        productImages: 'id, brandId, createdAt',
        generatedImages: 'id, jobId, predictionId, templateId, createdAt',
        resolvedPrompts: 'id, brandId, jobId, createdAt',
        userTemplates: 'id, source, createdAt',
      })
      .upgrade(async (tx) => {
        await tx.table('brands').toCollection().modify((b: { dna?: Record<string, unknown> }) => {
          if (!b.dna) return;
          delete b.dna.secondaryColor;
          delete b.dna.fontWeightHeadlines;
          delete b.dna.packaging;
          delete b.dna.ctaStyle;
          delete b.dna.layoutPreference;
          delete b.dna.competitors;
          delete b.dna.differentiation;
        });
      });
  }
}

let _db: BrandGeneratorDB | null = null;

export function getDB(): BrandGeneratorDB {
  if (!_db) _db = new BrandGeneratorDB();
  return _db;
}

/** Test-only — replaces the singleton (e.g., with fake-indexeddb). */
export function __setDB(db: BrandGeneratorDB | null): void {
  _db = db;
}
